import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { MedicalReferral } from "api/models/MedicalReferral";
import { RecordNoteHeader } from "routes/patients/PatientProfile/components/Information/components/RecordNoteHeader/RecordNoteHeader";
import { MUIMaskedInput, MUITextInput } from "shared/atoms/inputs";
import { MUIAccordion, MUIAccordionDetails, MUIAccordionSummary } from "shared/atoms/MUIAccordion";

import { ICDAutocomplete, SectionRow } from "../../../DynamicMedicalNotes/components";
import { Form } from "../../../DynamicMedicalNotes/utils/formStyles";

interface Props {
  patientId: number;
  record: MedicalReferral;
}

export const RecordReferral = ({ patientId, record }: Props): JSX.Element => {
  const { t } = useTranslation();
  const form = useForm({
    defaultValues: record,
  });

  return (
    <MUIAccordion>
      <MUIAccordionSummary>
        <RecordNoteHeader date={record.date_of_physician_signature}>
          {t("patients.medical_records.referral")}
        </RecordNoteHeader>
      </MUIAccordionSummary>
      <MUIAccordionDetails>
        <FormProvider {...form}>
          <Form>
            <ContentWrapper>
              <Section>
                <SectionRow>
                  <StyledMUITextInput name="doctor_name" label={t("patients.medical_records.responsible")} disabled />
                  <StyledMUIMaskedInput
                    name="duration_days"
                    label={t("patients.medical_records.referral_for")}
                    suffix={` ${t("common.days")}`}
                    disabled
                  />
                </SectionRow>
                <SectionRow>
                  <ICDAutocomplete
                    name="icd_codes"
                    label={t("patients.medical_records.diagnosis")}
                    patientId={patientId}
                    disabled
                  />
                </SectionRow>
                <SectionRow>
                  <MUITextInput name="reason" label={t("patients.medical_records.note")} multiline disabled />
                </SectionRow>
              </Section>
            </ContentWrapper>
          </Form>
        </FormProvider>
      </MUIAccordionDetails>
    </MUIAccordion>
  );
};

const StyledMUITextInput = styled(MUITextInput)`
  flex: 1;
  margin-right: ${props => props.theme.spacing.S_15};
`;

const StyledMUIMaskedInput = styled(MUIMaskedInput)`
  width: 125px;
  flex: none;

  ${props => props.theme.belowBreakpoint} {
    width: 100px;
  }
`;

const Section = styled.div`
  padding: ${props => props.theme.spacing.S_20};
  padding-right: 120px;

  ${props => props.theme.belowBreakpoint} {
    padding-right: ${props => props.theme.spacing.S_20};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
`;
